const BASE_URL = process.env.REACT_APP_API_URL || '';

async function request(path, options = {}, { skipAuthRedirect = false } = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  if (res.status === 401 && !skipAuthRedirect) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error((data && data.error) || `Request failed: ${res.status}`);
  }

  return data || {};
}

const api = {
  login: (email, password) =>
    request('/api/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  logout: () =>
    request('/api/logout', { method: 'POST' }),

  me: () =>
    request('/api/me', {}, { skipAuthRedirect: true }),

  getPortalCompanies: () =>
    request('/api/portal/companies'),

  getPortalAds: (companyId) =>
    request(`/api/portal/ads?company_id=${encodeURIComponent(companyId)}`),

  getPortalApplications: (companyId) =>
    request(`/api/portal/applications?company_id=${encodeURIComponent(companyId)}`),

  getPortalPlacementTypes: () =>
    request('/api/portal/placement-types'),

  createPortalAd: (data) =>
    request('/api/portal/ads', { method: 'POST', body: JSON.stringify(data) }),

  getPortalAd: (id) =>
    request(`/api/portal/ads/${id}`),

  updatePortalAd: (id, data) =>
    request(`/api/portal/ads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  updatePortalAdStatus: (id, status) =>
    request(`/api/portal/ads/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  updatePortalAdTargeting: (id, data) =>
    request(`/api/portal/ads/${id}/targeting`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getPortalFeatures: () =>
    request('/api/portal/features'),

  getPortalTemplates: (adId) =>
    request(`/api/portal/ads/${adId}/templates`),

  createPortalCreative: (adId, data) =>
    request(`/api/portal/ads/${adId}/creative`, { method: 'POST', body: JSON.stringify(data) }),

  getPortalCreative: (adId) =>
    request(`/api/portal/ads/${adId}/creative`),

  activatePortalAd: (id) =>
    request(`/api/portal/ads/${id}/activate`, { method: 'POST' }),

  pausePortalAd: (id) =>
    request(`/api/portal/ads/${id}/pause`, { method: 'POST' }),

  resumePortalAd: (id) =>
    request(`/api/portal/ads/${id}/resume`, { method: 'POST' }),

  getAdReport: (adId, startTime, endTime, timeUnit) =>
    request(`/api/portal/ads/${encodeURIComponent(adId)}/report?startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}${timeUnit ? '&timeUnit=' + encodeURIComponent(timeUnit) : ''}`),

  getAdsSummary: (companyId) =>
    request(`/api/portal/ads-summary?company_id=${encodeURIComponent(companyId)}`),

  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${BASE_URL}/api/portal/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // No Content-Type header — browser sets multipart boundary automatically
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.error) || `Upload failed: ${res.status}`);
    return data || {};
  },
};

export default api;
